import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { getOrganizationScanUsers } from '@kirin/api-client/api/scans';
import { apiClient } from '@/lib/api-client';
import { retryHandlers } from './helpers';
import type { ScanUsersResponse } from '@kirin/api-client/contracts/scans';

/**
 * Hook to fetch users associated with scans for a specific alert within an organization.
 *
 * The alert name is the composite `<subPolicyKey>::<type>` title key the Top
 * Alerts widget emits — render it with `formatTopAlertLabel(alertName,
 * revampEnabled)`, never raw, so a caller names the alert exactly as the
 * widget row the user clicked did.
 *
 * @param organizationId - The organization ID
 * @param alertName - The composite title key to filter by
 * @param enabled - Whether to enable the query
 * @param options - useQuery options
 *
 * @returns React Query result with scan users response
 */
export function useOrganizationScanUsers(
  organizationId: string | undefined,
  alertName: string | undefined,
  enabled = true,
  dateRange?: { from: string; to: string },
  options?: Omit<
    UseQueryOptions<ScanUsersResponse>,
    'queryKey' | 'queryFn' | 'enabled' | 'retry'
  >
): ReturnType<typeof useQuery<ScanUsersResponse>> {
  return useQuery<ScanUsersResponse>({
    queryKey: [
      'scans',
      'organization',
      organizationId ?? '',
      'users',
      alertName ?? '',
      dateRange?.from ?? '',
      dateRange?.to ?? '',
    ],
    queryFn: () => {
      if (!organizationId || !alertName) {
        throw new Error('organizationId and alertName are required');
      }
      return getOrganizationScanUsers(
        apiClient,
        organizationId,
        alertName,
        dateRange
      );
    },
    enabled: enabled && !!organizationId && !!alertName,
    retry: retryHandlers.detail,
    ...options,
  });
}
